import { FC, ReactNode, useState } from 'react'
import styled from 'styled-components'

type TabVariant = 'default' | 'pill'

interface Tab {
  id: string;
  label: string;
  component?: ReactNode;
  disabled?: boolean;
}

interface TabSelectorProps {
  tabs: Tab[];
  defaultTabId?: string;
  className?: string;
  marginTop?: string | number;
  marginBottom?: string | number;
  contentPadding?: string | number;
  tabPadding?: string | number;
  tabMargin?: string | number;
  onChange?: (tabId: string) => void;
  variant?: TabVariant;
  hideContent?: boolean;
  rightElement?: ReactNode;
  wrapperBackgroundColor?: string;
  activeTabColor?: string;
}

const TabSelector: FC<TabSelectorProps> = ({
  tabs,
  defaultTabId,
  className,
  marginTop,
  contentPadding,
  tabPadding,
  tabMargin,
  onChange,
  marginBottom,
  variant = 'default',
  hideContent = false,
  rightElement,
  wrapperBackgroundColor,
  activeTabColor,
}) => {
  const [ activeTabId, setActiveTabId ] = useState(defaultTabId || tabs[0]?.id)

  const activeTab = tabs.find(tab => tab.id === activeTabId)

  const handleTabChange = (tabId: string) => {
    const tab = tabs.find((item) => item.id === tabId)
    if (tab?.disabled) return
    setActiveTabId(tabId)
    onChange?.(tabId)
  }

  if (variant === 'pill') {
    return (
      <Container className={className} $marginTop={marginTop} $marginBottom={marginBottom}>
        <PillTabsContainer>
          {tabs.map((tab) => (
            <PillTab
              key={tab.id}
              $isActive={activeTabId === tab.id}
              $padding={tabPadding}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </PillTab>
          ))}
        </PillTabsContainer>
        {!hideContent && (
          <ContentContainer $padding={contentPadding}>
            {activeTab?.component}
          </ContentContainer>
        )}
      </Container>
    )
  }

  return (
    <Container className={className} $marginTop={marginTop} $marginBottom={marginBottom}>
      <TabsWrapper $margin={tabMargin} $backgroundColor={wrapperBackgroundColor}>
        <TabsContainer $insetTrack={!!wrapperBackgroundColor}>
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              $isActive={activeTabId === tab.id}
              $disabled={!!tab.disabled}
              $insetTrack={!!wrapperBackgroundColor}
              $padding={tabPadding}
              $activeColor={activeTabColor}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </Tab>
          ))}
        </TabsContainer>
        {rightElement && <RightElementWrapper>{rightElement}</RightElementWrapper>}
      </TabsWrapper>
      {!hideContent && (
        <ContentContainer $padding={contentPadding}>
          {activeTab?.component}
        </ContentContainer>
      )}
    </Container>
  )
}

const Container = styled.div<{ $marginTop?: string | number, $marginBottom?: string | number }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: ${({ $marginBottom }) =>
    $marginBottom
      ? typeof $marginBottom === 'number'
        ? `${$marginBottom}px`
        : $marginBottom
      : '0'
};
  margin-top: ${({ $marginTop }) =>
    $marginTop
      ? typeof $marginTop === 'number'
        ? `${$marginTop}px`
        : $marginTop
      : '0'
};
`

const TabsWrapper = styled.div<{ $margin?: string | number; $backgroundColor?: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: ${({ $margin }) =>
    $margin
      ? typeof $margin === 'number'
        ? `${$margin}px`
        : $margin
      : '0'
};
  margin-bottom: 8px;
  ${({ $backgroundColor }) => $backgroundColor && `
    background: ${$backgroundColor};
    padding: 3px;
    border-radius: 15px;
  `}
  position: sticky;
  top: 0;
  z-index: 2;
`

const TabsContainer = styled.div<{ $insetTrack?: boolean }>`
  display: flex;
  gap: 4px;
  min-height: 35px;
  background-color: ${({ $insetTrack }) => ($insetTrack ? 'transparent' : 'var(--bg-primary)')};
  border-radius: 12px;
  flex: 1;
`

const Tab = styled.button<{
  $isActive: boolean
  $disabled?: boolean
  $insetTrack?: boolean
  $padding?: string | number
  $activeColor?: string
}>`
  flex: 1;
  padding: ${({ $padding }) =>
    $padding
      ? typeof $padding === 'number'
        ? `${$padding}px`
        : $padding
      : '12px 24px'
};
  border: none;
  border-radius: 10px;
  font-size: var(--font-size-normal);
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.3s ease;
  background-color: ${({ $isActive, $activeColor }) => ($isActive ? ($activeColor ?? 'var(--bg-nonary)') : 'transparent')};
  color: ${({ $isActive, $disabled, $insetTrack }) => {
    if ($disabled) return 'var(--text-quaternary)'
    if ($isActive) return 'var(--r-color-white)'
    if ($insetTrack) return 'var(--text-quaternary)'
    return 'var(--text-primary)'
  }};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};

  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 0.45 : 0.8)};
  }
`

const PillTabsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const PillTab = styled.button<{ $isActive: boolean; $padding?: string | number }>`
  padding: ${({ $padding }) =>
    $padding
      ? typeof $padding === 'number'
        ? `${$padding}px`
        : $padding
      : '4px 10px'
};
  min-width: 80px;
  min-height: 32px;
  border: 1px solid ${({ $isActive }) => $isActive ? 'transparent' : 'var(--zinc-700, #3F3F46)'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${({ $isActive }) => $isActive ? 'var(--bg-nonary)' : 'transparent'};
  color: ${({ $isActive }) => $isActive ? 'var(--r-color-white)' : 'var(--text-quaternary, #A1A1AA)'};
  white-space: nowrap;

  &:hover {
    border-color: ${({ $isActive }) => $isActive ? 'transparent' : 'var(--zinc-600, #52525B)'};
  }
`

const RightElementWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
`

const ContentContainer = styled.div<{ $padding?: string | number }>`
  width: 100%;
  padding: ${({ $padding }) =>
    $padding
      ? typeof $padding === 'number'
        ? `${$padding}px`
        : $padding
      : '0'
};
  overflow-y: auto;
  flex: 1;
    &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 8px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--scrollbar-color);
    border-radius: 4px;
  }

  scrollbar-width: thin;
  scrollbar-color: var(--scrollbar-color) transparent;
`

export default TabSelector